import { useTranslate } from '@repo/shared/hooks'
import { Card, CenterBox, Col, Typography, Button } from '@repo/ui/components'
import { useEffect, useRef, useState } from 'react'
import { CancelButton } from './selfie-kyc.cancel-button'
import { RECORDING_DURATION } from './selfie-kyc.recording'
import { ISelfieKycStartProps } from './selfie-kyc.types'

const SelfieKycStart = ({
  onNext,
  onRequestCameraAccess,
  hasCameraPermission,
  isRequestingAccess = false,
}: ISelfieKycStartProps) => {
  const { tRich, t } = useTranslate()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)

  useEffect(() => {
    if (
      hasCameraPermission &&
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia
    ) {
      const requestMedia = async (retryCount = 0) => {
        const MAX_RETRIES = 2
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true,
          })
          setMediaStream(stream)
          if (videoRef.current) {
            videoRef.current.srcObject = stream
          }
        } catch (err: any) {
          console.log('🚀 ~ useEffect ~ err:', err)

          // Retry for certain errors that might be temporary
          if (
            (err?.name === 'NotReadableError' ||
              err?.name === 'TrackStartError' ||
              err?.name === 'AbortError') &&
            retryCount < MAX_RETRIES
          ) {
            setTimeout(
              () => {
                requestMedia(retryCount + 1)
              },
              1000 * (retryCount + 1),
            )
          }
          // For permission errors, don't retry - user needs to grant permission
          // The permission state will be updated by the parent component
        }
      }

      requestMedia()
    }
  }, [hasCameraPermission])

  useEffect(() => {
    if (videoRef.current && mediaStream) {
      videoRef.current.srcObject = mediaStream
    }
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => {
          track.stop()
          track.enabled = false
        })
      }
    }
  }, [mediaStream])

  return (
    <Card
      className='!p-0 overflow-hidden relative h-[100dvh]  rounded-none border-none'
      style={{ maxHeight: '100dvh', minHeight: '100dvh', height: '100dvh' }}
    >
      <CancelButton />
      <CenterBox className='text-center h-full w-full bg-black text-white overflow-hidden'>
        {hasCameraPermission ? (
          <div className='relative w-full h-full'>
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className='bg-black w-full aspect-[9/16] object-cover h-full'
              style={{
                maxHeight: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
            <div className='absolute inset-0 bg-black/50 mask-ellipse' />
          </div>
        ) : (
          <Col className='items-center gap-md px-4xl'>
            <Typography className='leading-8'>
              {t('selfie_kyc_permission_title')}
            </Typography>
            <Button
              variant='ghost'
              size='xs'
              type='button'
              onClick={onRequestCameraAccess}
              disabled={isRequestingAccess}
            >
              {isRequestingAccess
                ? t('selfie_kyc_camera_access_requesting')
                : t('selfie_kyc_camera_access_request')}
            </Button>
          </Col>
        )}
      </CenterBox>
      <Col className='p-md gap-md w-full items-center'>
        <Typography variant='p' size='md' className='text-center'>
          {tRich.rich('selfie_kyc_start_guide', {
            b: (chunks) => (
              <span className='bg-base-surface-20 text-neutral-surface-100'>
                {chunks}
              </span>
            ),
            br: () => <br />,
            seconds: RECORDING_DURATION,
          })}
        </Typography>

        <Button
          variant='fill'
          color='base'
          size='lg'
          width='full'
          onClick={onNext}
          disabled={!hasCameraPermission}
        >
          {t('selfie_kyc_start_btn')}
        </Button>
      </Col>
    </Card>
  )
}

export { SelfieKycStart }
